import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, makeStyles } from '@material-ui/core';
import { addSnackbar } from '../features/snackbar/actions';
import { onUpdateAvailable, reloadForBuild, reloadNow } from '../scripts/version-check';

const useStyles = makeStyles((theme) => ({
  // One centred row, so the text lines up with the Alert's icon and the Reload button with both.
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  reloadButton: {
    marginLeft: theme.spacing(2),
    // An outlined button's default border is a dark grey meant for light backgrounds; on the
    // filled Alert both the border and the label are white so it reads as a button.
    color: theme.palette.common.white,
    borderColor: theme.palette.common.white,
    // A small button is taller than a line of Alert text; without this it pushes the row taller
    // than the icon beside it and the text sits low against the icon.
    marginTop: -theme.spacing(0.75),
    marginBottom: -theme.spacing(0.75),
  },
}));

/**
 * Once scripts/version-check.ts finds that a newer build has been deployed, offers a reload and
 * takes one at the next navigation -- a page change is a moment nobody is mid-edit, so it costs
 * nothing. Must render inside the Router whose history the app navigates with.
 */
export default function VersionUpdateNotice() {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  useEffect(() => {
    let stopListening: (() => void) | undefined;
    const unsubscribe = onUpdateAvailable((latest) => {
      dispatch(
        addSnackbar({
          id: Date.now(),
          severity: 'info',
          autoHideDuration: null,
          // Reload is the only way out: dismissing the notice would leave the tab on the old build
          // with nothing left to say so.
          dismissible: false,
          message: (
            <span className={classes.message}>
              A new version of CENTERLINE is available.
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={reloadNow}
                className={classes.reloadButton}
              >
                Reload
              </Button>
            </span>
          ),
        }),
      );
      stopListening?.();
      stopListening = history.listen(() => reloadForBuild(latest));
    });
    return () => {
      unsubscribe();
      stopListening?.();
    };
  }, [history, classes]);

  return null;
}
